import type { ThreadExpanded } from "@brookbush/types";

import { Chat } from "@ai-sdk/react";
import { useAuth } from "@clerk/nextjs";
import { DefaultChatTransport, type UIMessage } from "ai";
import posthog from "posthog-js";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSWRConfig } from "swr";

import type { QuizResults } from "~/components/Chat/ChatQuiz";
import useGlobalStore from "~/hooks/useGlobalStore";
import useThread from "~/hooks/useThread";
import useThreads from "~/hooks/useThreads";
import { madeStudyRecommendation } from "~/lib/chat/madeStudyRecommendation";

export type ChatView = "conversation" | "history";

export interface ChatController {
	isSignedIn: boolean;
	activeChatThreadId: number | null;
	thread: ThreadExpanded | undefined;
	isLoading: boolean;
	chat: Chat<UIMessage> | null;
	view: ChatView;
	setView: (view: ChatView) => void;
	draft: string;
	setDraft: (draft: string) => void;
	inputRef: RefObject<HTMLInputElement | null>;
	isSendDisabled: boolean;
	handleSubmit: () => Promise<void>;
	handleSelectThread: (threadId: number) => void;
	handleNewChat: () => void;
	handleClose: () => void;
	handleStatusChange: (status: string) => void;
	handleSendPrompt: (promptText: string) => Promise<void>;
	handleSendQuizResult: (results: QuizResults) => Promise<void>;
}

const useChatController = (): ChatController => {
	const { isSignedIn, getToken } = useAuth();
	const { mutate: globalMutate } = useSWRConfig();
	const {
		activeChatThreadId,
		setActiveChatThreadId,
		closeChatBar,
		collapseChat,
		isChatImmersive,
		chatPageContent,
	} = useGlobalStore();

	const [view, setView] = useState<ChatView>("conversation");
	const [draft, setDraft] = useState("");
	const [autoSendMessage, setAutoSendMessage] = useState<string | undefined>();
	const [isStarting, setIsStarting] = useState(false);
	const [isChatBusy, setIsChatBusy] = useState(false);

	const inputRef = useRef<HTMLInputElement>(null);

	const { createThread } = useThreads(false);
	const { thread, initialMessages, isLoading } = useThread(activeChatThreadId);

	// One Chat instance per thread, owned here (at the ChatWidget level) so
	// messages and in-flight streams survive the sidebar/immersive and
	// conversation/history remounts of the components below.
	const chatRef = useRef<{ threadId: number; chat: Chat<UIMessage> } | null>(
		null,
	);
	if (
		activeChatThreadId !== null &&
		chatRef.current?.threadId !== activeChatThreadId
	) {
		const threadId = activeChatThreadId;
		chatRef.current = {
			threadId,
			chat: new Chat<UIMessage>({
				id: String(threadId),
				messages: initialMessages,
				transport: new DefaultChatTransport({
					api: `${process.env.NEXT_PUBLIC_CHAT_API_URL}/threads/${threadId}/messages`,
					headers: async (): Promise<Record<string, string>> => {
						const token = await getToken();
						return token ? { Authorization: `Bearer ${token}` } : {};
					},
				}),
				onFinish: ({ message }) => {
					globalMutate(`/threads/${threadId}`);

					// The tutor changed the recommended sections: reload them on whichever course page is open
					if (madeStudyRecommendation(message)) {
						globalMutate(
							(key) =>
								typeof key === "string" &&
								key.endsWith("/recommended-sections"),
						);
					}
				},
			}),
		};
	}
	const chat =
		activeChatThreadId !== null ? (chatRef.current?.chat ?? null) : null;

	// The Chat instance reads its seed messages only at construction — sync
	// messages that arrive from the server afterwards (initial SWR resolve or
	// revalidation) while the chat is idle.
	useEffect(() => {
		if (!chat) return;
		if (chat.status === "submitted" || chat.status === "streaming") return;
		if (initialMessages.length > chat.messages.length) {
			chat.messages = initialMessages;
		}
	}, [chat, initialMessages]);

	// Send the pending first message once its thread's chat exists, clearing it
	// first so remounts and thread switches can never replay it.
	useEffect(() => {
		if (!chat || !autoSendMessage) return;
		setAutoSendMessage(undefined);
		chat.sendMessage({ text: autoSendMessage });
	}, [chat, autoSendMessage]);

	const handleStatusChange = useCallback((status: string) => {
		setIsChatBusy(status === "submitted" || status === "streaming");
	}, []);

	useEffect(() => {
		setIsChatBusy(false);
		if (activeChatThreadId) setView("conversation");
	}, [activeChatThreadId]);

	// Esc steps down one level: immersive -> sidebar, sidebar -> closed
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;
			if (isChatImmersive) collapseChat();
			else closeChatBar();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [isChatImmersive, collapseChat, closeChatBar]);

	const handleClose = useCallback(() => {
		closeChatBar();
		setAutoSendMessage(undefined);
	}, [closeChatBar]);

	// Selecting the already-active thread doesn't change activeChatThreadId,
	// so the view must be switched explicitly rather than via the effect above.
	const handleSelectThread = useCallback(
		(threadId: number) => {
			setActiveChatThreadId(threadId);
			setView("conversation");
		},
		[setActiveChatThreadId],
	);

	const handleNewChat = useCallback(() => {
		setActiveChatThreadId(null);
		setAutoSendMessage(undefined);
		setDraft("");
		setView("conversation");
		inputRef.current?.focus();
	}, [setActiveChatThreadId]);

	const handleSubmit = async () => {
		const text = draft.trim();
		if (!text) return;

		// Send to the existing thread's chat directly
		if (chat) {
			if (chat.status === "submitted" || chat.status === "streaming") return;
			chat.sendMessage({ text });
			setDraft("");
			return;
		}

		if (!isSignedIn || isStarting) return;

		setIsStarting(true);
		try {
			const newThread = await createThread(
				chatPageContent
					? {
							contentType: chatPageContent.contentType,
							contentId: chatPageContent.contentId,
						}
					: undefined,
			);
			posthog.capture("ai_tutor:typed_prompt_started");
			setActiveChatThreadId(newThread.id);
			setAutoSendMessage(text);
			setDraft("");
		} finally {
			setIsStarting(false);
		}
	};

	const handleSendPrompt = useCallback(
		async (promptText: string) => {
			const text = promptText.trim();
			if (!text) return;

			if (chat) {
				if (chat.status === "submitted" || chat.status === "streaming") return;
				chat.sendMessage({ text });
				return;
			}

			if (!isSignedIn || isStarting) return;

			setIsStarting(true);
			try {
				const newThread = await createThread(
					chatPageContent
						? {
								contentType: chatPageContent.contentType,
								contentId: chatPageContent.contentId,
							}
						: undefined,
				);
				setActiveChatThreadId(newThread.id);
				setAutoSendMessage(text);
			} finally {
				setIsStarting(false);
			}
		},
		[
			chat,
			isSignedIn,
			isStarting,
			createThread,
			chatPageContent,
			setActiveChatThreadId,
		],
	);

	const handleSendQuizResult = useCallback(
		async (results: QuizResults) => {
			if (!chat) return;
			const message = `The user completed the quiz with a score of ${
				results.score
			}. Incorrect questions: ${JSON.stringify(results.incorrectQuestions)}`;
			await chat.sendMessage({
				text: message,
				metadata: {
					type: "quizResult",
					...results,
				},
			});
		},
		[chat],
	);

	const isSendDisabled = activeChatThreadId
		? isChatBusy
		: isStarting || !draft.trim();

	return {
		isSignedIn: !!isSignedIn,
		activeChatThreadId,
		thread,
		isLoading,
		chat,
		view,
		setView,
		draft,
		setDraft,
		inputRef,
		isSendDisabled,
		handleSubmit,
		handleSelectThread,
		handleNewChat,
		handleClose,
		handleStatusChange,
		handleSendPrompt,
		handleSendQuizResult,
	};
};

export default useChatController;
